/**
 * IndexedDB utilities for offline data storage
 * Stores clients, estimates, jobs, and org settings for offline access
 */

const DB_NAME = 'birden-offline-db';
const DB_VERSION = 3;

// Store names
const STORES = {
  CLIENTS: 'clients',
  ESTIMATES: 'estimates',
  JOBS: 'jobs',
  INVOICES: 'invoices',
  SITE_VISITS: 'site_visits',
  FIELD_NOTES_DRAFTS: 'field_notes_drafts',
  ORG_SETTINGS: 'org_settings',
  USER_SETTINGS: 'user_settings',
  OFFLINE_QUEUE: 'offline_queue',
  SYNC_META: 'sync_meta',
  PHOTO_UPLOADS: 'photo_uploads',     // NEW v3: dedicated store for pending photo uploads
  SYNC_LOGS: 'sync_logs',              // NEW v3: structured sync diagnostic logs
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.log('[OfflineDB] Upgrading database...');

      // Clients store
      if (!database.objectStoreNames.contains(STORES.CLIENTS)) {
        const clientStore = database.createObjectStore(STORES.CLIENTS, { keyPath: 'id' });
        clientStore.createIndex('org_id', 'org_id', { unique: false });
        clientStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      // Estimates store
      if (!database.objectStoreNames.contains(STORES.ESTIMATES)) {
        const estimateStore = database.createObjectStore(STORES.ESTIMATES, { keyPath: 'id' });
        estimateStore.createIndex('org_id', 'org_id', { unique: false });
        estimateStore.createIndex('client_id', 'client_id', { unique: false });
        estimateStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Jobs store
      if (!database.objectStoreNames.contains(STORES.JOBS)) {
        const jobStore = database.createObjectStore(STORES.JOBS, { keyPath: 'id' });
        jobStore.createIndex('org_id', 'org_id', { unique: false });
        jobStore.createIndex('client_id', 'client_id', { unique: false });
        jobStore.createIndex('start_date', 'start_date', { unique: false });
      }

      // Invoices store
      if (!database.objectStoreNames.contains(STORES.INVOICES)) {
        const invoiceStore = database.createObjectStore(STORES.INVOICES, { keyPath: 'id' });
        invoiceStore.createIndex('org_id', 'org_id', { unique: false });
        invoiceStore.createIndex('client_id', 'client_id', { unique: false });
      }

      // Site visits store
      if (!database.objectStoreNames.contains(STORES.SITE_VISITS)) {
        const siteVisitStore = database.createObjectStore(STORES.SITE_VISITS, { keyPath: 'id' });
        siteVisitStore.createIndex('org_id', 'org_id', { unique: false });
        siteVisitStore.createIndex('client_id', 'client_id', { unique: false });
        siteVisitStore.createIndex('scheduled_date', 'scheduled_date', { unique: false });
      }

      // Field notes drafts store (for offline editing)
      if (!database.objectStoreNames.contains(STORES.FIELD_NOTES_DRAFTS)) {
        const draftsStore = database.createObjectStore(STORES.FIELD_NOTES_DRAFTS, { keyPath: 'id' });
        draftsStore.createIndex('site_visit_id', 'site_visit_id', { unique: false });
        draftsStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      // Org settings store
      if (!database.objectStoreNames.contains(STORES.ORG_SETTINGS)) {
        database.createObjectStore(STORES.ORG_SETTINGS, { keyPath: 'id' });
      }

      // User settings store
      if (!database.objectStoreNames.contains(STORES.USER_SETTINGS)) {
        database.createObjectStore(STORES.USER_SETTINGS, { keyPath: 'id' });
      }

      // Offline queue store (for pending actions)
      if (!database.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = database.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('created_at', 'created_at', { unique: false });
        queueStore.createIndex('action_type', 'action_type', { unique: false });
      }

      // Sync metadata store
      if (!database.objectStoreNames.contains(STORES.SYNC_META)) {
        database.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
      }

      // Photo uploads store (NEW v3) — base64 blobs live here, isolated from main queue
      if (!database.objectStoreNames.contains(STORES.PHOTO_UPLOADS)) {
        const photoStore = database.createObjectStore(STORES.PHOTO_UPLOADS, { keyPath: 'id' });
        photoStore.createIndex('site_visit_id', 'site_visit_id', { unique: false });
        photoStore.createIndex('status', 'status', { unique: false });
        photoStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Sync diagnostic logs store (NEW v3) — capped to last 200 entries
      if (!database.objectStoreNames.contains(STORES.SYNC_LOGS)) {
        const logsStore = database.createObjectStore(STORES.SYNC_LOGS, { keyPath: 'id', autoIncrement: true });
        logsStore.createIndex('timestamp', 'timestamp', { unique: false });
        logsStore.createIndex('level', 'level', { unique: false });
      }
    };
  });
}

/**
 * Get all items from a store
 */
export async function getAll(storeName) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get items by index
 */
export async function getByIndex(storeName, indexName, value) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a single item by key
 */
export async function get(storeName, key) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Put (add or update) an item
 */
export async function put(storeName, item) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Put multiple items
 */
export async function putMany(storeName, items) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    let completed = 0;
    items.forEach((item) => {
      const request = store.put(item);
      request.onsuccess = () => {
        completed++;
        if (completed === items.length) {
          resolve(completed);
        }
      };
      request.onerror = () => reject(request.error);
    });

    if (items.length === 0) {
      resolve(0);
    }
  });
}

/**
 * Delete an item
 */
export async function remove(storeName, key) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all items from a store
 */
export async function clear(storeName) {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Export store names for use in other files
export { STORES };
